import * as XLSX from 'xlsx'

/**
 * Apple'ın "Distributor Enrollment Form" şablonunu okur.
 *
 * Şablon düzeni: birinci sayfada etiket A sütununda, değer sağındaki ilk dolu
 * hücrede; ikinci sayfada seri numaraları alt alta. Apple'ın orijinal formunda
 * bazı değerler B yerine C/D sütununda olabildiği için satırdaki ilk dolu hücre
 * aranır. Etiket eşleşmesi büyük/küçük harf ve Türkçe karakter duyarsızdır.
 *
 * Dosya tarayıcıda ayrıştırılır ama <b>doğrulama sunucuda</b> yapılır: seri
 * numaraları SAP'ten gelen cihaz havuzuyla eşleştirilir, havuzda olmayan satır
 * reddedilir. Buradaki ayrıştırma yalnızca kullanıcıya önizleme göstermek ve
 * alanları JSON'a çevirmek içindir.
 */

export interface IAppleDepParsedTemplate {
  CustomerName: string
  OrganizationId: string
  SalesOrderNumber: string
  SalesOrderDate: string
  PoNumber: string
  TransactionType: string
  SerialNumbers: string[]
}

/** Türkçe karakterleri ASCII'ye indirger, büyük harfe çevirir, boşlukları sadeleştirir. */
const normalizeLabel = (value: string): string => {
  const map: Record<string, string> = {
    ı: 'I', İ: 'I', i: 'I', ş: 'S', Ş: 'S', ğ: 'G', Ğ: 'G',
    ü: 'U', Ü: 'U', ö: 'O', Ö: 'O', ç: 'C', Ç: 'C',
  }

  return (value ?? '')
    .split('')
    .map(ch => map[ch] ?? ch.toUpperCase())
    .join('')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
}

/**
 * Şablondaki etiketler. Kısmi eşleşme yeter, böylece küçük metin farkları tolere edilir.
 *
 * <b>exclude</b> gerekli: Apple'ın formunda birbirine benzeyen etiketler var ve yalnızca
 * "içerir" mantığı yanlış alanı yakalıyor —
 * "Sales Order #" ile "Date of Sales Order", ayrıca Section 1'deki
 * "Hardware Purchase Order #" (bayinin Index'e kestiği sipariş) ile Section 2'deki
 * "Customer (End-User) Purchase Order #" (Apple'a gidecek olan) karışıyor.
 */
interface ILabelRule {
  include: string[]
  exclude?: string[]
}

const LABELS: Record<string, ILabelRule> = {
  customerName: { include: ['END USER ORGANIZATION NAME', 'END USER ORGANISATION NAME'] },
  organizationId: { include: ['ORGANISATION ID', 'ORGANIZATION ID'] },
  // "Date of Sales Order" da "SALES ORDER" içerdiği için tarih satırı elenir.
  salesOrderNumber: { include: ['SALES ORDER'], exclude: ['DATE'] },
  salesOrderDate: { include: ['DATE OF SALES ORDER'] },
  // Apple'a giden PO son kullanıcınınki; Section 1'deki donanım PO'su değil.
  poNumber: { include: ['END USER PURCHASE ORDER'] },
  // Bölüm başlığı ("Section 3: Transaction Type...") değil, veri satırı.
  transactionType: { include: ['TRANSACTION TYPE'], exclude: ['SECTION'] },
}

type SheetRow = (string | number | null | undefined)[]

const cellText = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  return `${value}`.trim()
}

/**
 * Excel tarih hücreleri sayı olarak gelebilir; `cellDates` ile Date'e çevrilenleri
 * ISO'ya, metin olanları olduğu gibi döndürür.
 */
const cellDateText = (value: unknown): string => {
  if (value instanceof Date && !isNaN(value.getTime())) {
    const year = value.getFullYear()
    const month = `${value.getMonth() + 1}`.padStart(2, '0')
    const day = `${value.getDate()}`.padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return cellText(value as string)
}

/** Etiketi satır başında arar, satırdaki sonraki ilk dolu hücreyi değer kabul eder. */
const findValue = (rows: SheetRow[], rule: ILabelRule, asDate = false): string => {
  for (const row of rows) {
    if (!row || row.length === 0) continue

    const label = normalizeLabel(cellText(row[0]))
    if (!label) continue

    if (!rule.include.some(keyword => label.includes(keyword))) continue
    if (rule.exclude?.some(keyword => label.includes(keyword))) continue

    for (let i = 1; i < row.length; i++) {
      const raw = row[i]
      const text = asDate ? cellDateText(raw) : cellText(raw)
      if (text) return text
    }
  }

  return ''
}

/**
 * Seri numarası sayfasını bulur. Adında "serial"/"seri" geçen sayfa tercih edilir;
 * yoksa ikinci sayfaya düşülür.
 */
const findSerialSheetName = (workbook: XLSX.WorkBook): string | undefined => {
  const byName = workbook.SheetNames.find(name => {
    const normalized = normalizeLabel(name)
    return normalized.includes('SERIAL') || normalized.includes('SERI')
  })

  return byName ?? workbook.SheetNames[1]
}

/** Başlık satırlarını atlayıp seri numarası gibi görünen hücreleri toplar. */
const collectSerialNumbers = (rows: SheetRow[]): string[] => {
  const serials: string[] = []
  const seen = new Set<string>()

  for (const row of rows) {
    if (!row) continue

    for (const raw of row) {
      const text = cellText(raw)
      if (!text) continue

      const normalized = normalizeLabel(text)

      // Başlık ve açıklama satırlarını ele
      if (normalized.includes('SERIAL') || normalized.includes('SERI NO') || normalized.includes('IMEI')) continue
      // Apple deviceId en fazla 20 karakter; daha uzunu açıklama metnidir
      if (text.length > 20) continue

      const value = text.toUpperCase()
      if (seen.has(value)) continue

      seen.add(value)
      serials.push(value)
    }
  }

  return serials
}

export const parseAppleDepTemplate = async (file: File): Promise<IAppleDepParsedTemplate> => {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true })

  if (workbook.SheetNames.length === 0) {
    throw new Error('Dosyada sayfa bulunamadı.')
  }

  const formSheet = workbook.Sheets[workbook.SheetNames[0]]
  const formRows = XLSX.utils.sheet_to_json<SheetRow>(formSheet, { header: 1, blankrows: false, defval: '' })

  const serialSheetName = findSerialSheetName(workbook)
  const serialRows = serialSheetName
    ? XLSX.utils.sheet_to_json<SheetRow>(workbook.Sheets[serialSheetName], {
        header: 1,
        blankrows: false,
        defval: '',
      })
    : []

  return {
    CustomerName: findValue(formRows, LABELS.customerName),
    OrganizationId: findValue(formRows, LABELS.organizationId),
    SalesOrderNumber: findValue(formRows, LABELS.salesOrderNumber),
    SalesOrderDate: findValue(formRows, LABELS.salesOrderDate, true),
    PoNumber: findValue(formRows, LABELS.poNumber),
    TransactionType: findValue(formRows, LABELS.transactionType),
    SerialNumbers: collectSerialNumbers(serialRows),
  }
}

/** Şablonun indirileceği statik adres. */
export const APPLE_DEP_TEMPLATE_URL = '/files/apple-dep-yukleme-sablonu.xlsx'
